import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Check, Star, ArrowRight, MessageCircle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

const plans = [
  {
    name: "Landing Page Express",
    price: "897",
    priceInCents: 89700,
    desc: "Ideal para anúncios.",
    delivery: "Entrega em até 72 horas",
    popular: false,
    cta: "Criar minha Landing Page",
    features: [
      "Landing page de alta conversão",
      "Formulário de leads",
      "Botão WhatsApp",
      "Design responsivo",
      "Estrutura pronta para rodar anúncios",
    ],
  },
  {
    name: "Site Express",
    price: "1.497",
    priceInCents: 149700,
    desc: "Site institucional simples.",
    delivery: "Entrega em até 5 dias",
    popular: false,
    cta: "Criar meu site",
    features: [
      "Até 4 páginas",
      "Formulário de contato",
      "Botão WhatsApp",
      "Design profissional",
      "SEO básico",
    ],
  },
  {
    name: "Site + Landing Page",
    price: "2.497",
    priceInCents: 249700,
    desc: "Solução completa.",
    delivery: "Entrega em até 7 dias",
    popular: true,
    cta: "Criar meu site agora",
    features: [
      "Site institucional completo",
      "Landing page de conversão",
      "Formulário de leads",
      "Botão WhatsApp",
      "Estrutura pronta para anúncios",
    ],
  },
];

const PlansSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

  const handleCheckout = async (planName: string, priceInCents: number) => {
    setLoadingPlan(planName);
    try {
      const { data, error } = await supabase.functions.invoke("create-pagarme-payment", {
        body: { planName, priceInCents },
      });
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (err) {
      console.error("Checkout error:", err);
      toast({
        title: "Erro ao iniciar pagamento",
        description: "Tente novamente ou entre em contato via WhatsApp.",
        variant: "destructive",
      });
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <section id="planos" className="py-24 md:py-32 relative" ref={ref}>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-primary/5 blur-3xl" />
      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <span className="text-sm font-mono text-secondary uppercase tracking-widest">Planos</span>
          <h2 className="text-3xl md:text-5xl font-bold mt-4">
            Escolha o plano ideal para <span className="gradient-text">seu negócio.</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((plan, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 + i * 0.1 }}
              className={`rounded-2xl p-8 relative flex flex-col ${
                plan.popular
                  ? "glass-card border-primary/50 glow-blue"
                  : "glass-card"
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-primary text-primary-foreground text-xs font-bold px-4 py-1.5 rounded-full">
                  <Star className="w-3 h-3" /> Mais popular
                </div>
              )}
              <h3 className="font-bold text-lg">{plan.name}</h3>
              <p className="text-sm text-muted-foreground mt-1 mb-4">{plan.desc}</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-sm text-muted-foreground">R$</span>
                <span className="text-4xl font-bold">{plan.price}</span>
              </div>
              <p className="text-xs text-muted-foreground mb-1">
                ou em até <span className="font-semibold text-foreground">10x no cartão</span> (3x sem juros)
              </p>
              <p className="text-xs text-secondary font-medium mb-6">{plan.delivery}</p>
              <ul className="space-y-3 mb-8 flex-1">
                {plan.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                id={`btn-checkout-${plan.name.toLowerCase().replace(/\s+/g, '-').normalize('NFD').replace(/[\u0300-\u036f]/g, '')}`}
                onClick={() => handleCheckout(plan.name, plan.priceInCents)}
                disabled={loadingPlan === plan.name}
                className={`flex items-center justify-center gap-2 rounded-xl py-3.5 w-full text-sm font-semibold transition-all disabled:opacity-70 ${
                  plan.popular
                    ? "bg-primary text-primary-foreground hover:opacity-90 glow-blue"
                    : "glass hover:bg-muted text-foreground"
                }`}
              >
                {loadingPlan === plan.name ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    {plan.cta}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6 }}
          className="mt-14 text-center max-w-md mx-auto"
        >
          <p className="text-lg font-semibold text-foreground">
            Ficou com dúvidas sobre qual plano escolher?
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            Fale com nossa equipe e receba uma orientação rápida para o seu negócio.
          </p>
          <a
            id="btn-whatsapp-planos"
            href="https://wa.me/5519988278133?text=Olá,%20quero%20criar%20um%20site%20pela%20Ad4You%20Pages"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-5 px-6 py-3 rounded-xl bg-[#25D366] text-white font-semibold text-sm hover:opacity-90 transition-all"
          >
            <MessageCircle className="w-4 h-4" />
            Falar no WhatsApp
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.7 }}
          className="mt-12 glass-card rounded-2xl p-6 flex items-center gap-4 text-left max-w-lg mx-auto"
        >
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 shrink-0">
            <MessageCircle className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">Atendimento rápido e humano</p>
            <p className="text-xs text-muted-foreground mt-1">
              Nosso atendimento é feito por profissionais da equipe Ad4You, prontos para entender seu negócio e orientar na criação da página ideal. Aqui você fala com pessoas reais, sem robôs ou respostas automáticas.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PlansSection;
